import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableTextInput } from '@/components/ui-v2/inputs/table/table-text-input';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { createSubRowSortingFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { Row } from '@tanstack/react-table';
import { ChevronDown, ChevronRight, SquarePen } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
}

interface NameCellContentProps {
  row: Row<DealItemWithVersions>;
  handleBlur: ColumnProps['handleBlur'];
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
}

function NameCellContent({
  row,
  handleBlur,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
}: NameCellContentProps) {
  const { t } = useTranslation();
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'name');
  const isParent = row.subRows.length > 1;

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="name"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div
          className={cn(
            'h-full w-full',
            isParent || !isOfferLocked
              ? 'cursor-pointer'
              : 'cursor-not-allowed',
            isOfferLocked && 'opacity-50',
            !isParent &&
              isRecentlyModified &&
              'border-l-2 border-blue-500 bg-blue-50'
          )}
          onClick={() =>
            !isParent && handleCellClick(row.original.activeVersion.id, 'name')
          }
        >
          {isEditing ? (
            <TableTextInput
              value={row.original.activeVersion.name}
              onChange={async (value) => {
                await handleCellEdit(
                  row.original.activeVersion.id,
                  'name',
                  value ?? '',
                  !isParent
                );
              }}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              disabled={isOfferLocked}
              autoFocus={isEditing}
            />
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="h-full w-full">
                  <div
                    className={cn(
                      'flex h-full w-full items-center justify-between px-2 text-sm'
                    )}
                  >
                    {isParent ? (
                      <div className="flex flex-1 items-center truncate text-sm">
                        {row.original.activeVersion.name || ''}
                      </div>
                    ) : typeof row.parentId !== 'undefined' ? (
                      (() => {
                        const options = (
                          row.original.activeVersion.options ?? []
                        ).filter((option) => option.name !== '_index');

                        if (options.length > 0) {
                          return (
                            <div className="group flex w-full items-center gap-1 p-1">
                              {!isOfferLocked && (
                                <button
                                  className="invisible flex items-center gap-1 rounded-sm border border-gray-400 bg-white px-1.5 py-1 text-sm font-medium text-gray-800 transition-opacity duration-200 group-hover:visible"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCellClick(
                                      row.original.activeVersion.id,
                                      'name'
                                    );
                                  }}
                                >
                                  <SquarePen className="h-3 w-3" />
                                  {t('kpi.name_modify')}
                                </button>
                              )}
                              <div className="scrollbar-hide flex flex-1 justify-end gap-1 overflow-x-auto">
                                {options.map((option, index) => (
                                  <Badge
                                    key={`${option.name}-${index}`}
                                    variant="outline"
                                    className="block max-w-[70px] cursor-default truncate rounded-sm border bg-gray-700 px-1.5 py-1 text-xs font-medium text-white"
                                  >
                                    {option.value}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <div className="flex-1 truncate text-sm">
                              {row.original.activeVersion.name || ''}
                            </div>
                          );
                        }
                      })()
                    ) : (
                      (() => {
                        const options = (
                          row.original.activeVersion.options ?? []
                        ).filter((option) => option.name !== '_index');

                        if (options.length > 0) {
                          return (
                            <div className="flex-1 truncate text-sm">
                              {`${row.original.activeVersion.name || ''} - ${options
                                .map((option) => option.value)
                                .join(' - ')}`}
                            </div>
                          );
                        } else {
                          return (
                            <div className="flex-1 truncate text-sm">
                              {row.original.activeVersion.name || ''}
                            </div>
                          );
                        }
                      })()
                    )}

                    {isParent &&
                      (() => {
                        const filteredVariantCount = row.subRows.length;
                        if (filteredVariantCount <= 1) return null;
                        return (
                          <div className="ml-2 flex flex-shrink-0 items-center gap-1">
                            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs text-blue-800">
                              {filteredVariantCount}
                            </span>
                            <button className="flex-shrink-0 rounded-full bg-blue-100 p-1 text-blue-800 transition-colors group-hover/row:bg-blue-200 group-hover/row:text-gray-800">
                              {row.getIsExpanded() ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        );
                      })()}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">
                  {isParent
                    ? row.original.activeVersion.name || ''
                    : typeof row.parentId !== 'undefined'
                      ? `${row.original.activeVersion.name || ''} - ${(
                          row.original.activeVersion.options ?? []
                        )
                          .filter((option) => option.name !== '_index')
                          .map((opt) => opt.value)
                          .join(' - ')}`
                      : (() => {
                          const options = (
                            row.original.activeVersion.options ?? []
                          ).filter((option) => option.name !== '_index');
                          if (options.length > 0) {
                            return `${row.original.activeVersion.name || ''} - ${options
                              .map((opt) => opt.value)
                              .join(' - ')}`;
                          }
                          return row.original.activeVersion.name || '';
                        })()}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefName = ({
  handleBlur,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'name';
  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'Name',
      enableGrouping: true,
      accessorFn: (row) => row.activeVersion?.name ?? '',
      getGroupingValue: (row) => row.activeVersion?.name ?? '',
      sortingFn: createSubRowSortingFn(
        'name',
        (row) => row.activeVersion?.name ?? null
      ),
      header: () => (
        <div
          className="flex items-center pl-2"
          onClick={() => setColumnSort(accessorKey)}
        >
          {t('kpi.name')} <SortIcon pointer="name" />
        </div>
      ),
      cell: ({ row }) => (
        <NameCellContent
          row={row}
          handleBlur={handleBlur}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
        />
      ),
      size: 320,
    }),
    [
      t,
      setColumnSort,
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    ]
  );
};

export default useColumnDefName;
